import { getColorInCherryFormat, getWeekKey } from './functions';
import type { BoxFormatMapping, Exporter } from './types';

// Frutic is enabled per exporter (hasFrutic) and can be limited to specific
// seasons (frutic_seasons). No seasons listed means every season.
export const exporterUsesFrutic = (
  exporter: Pick<Exporter, 'hasFrutic' | 'frutic_seasons'> | null | undefined,
  season: string | null | undefined
): boolean => {
  if (!exporter?.hasFrutic) return false;
  if (!exporter.frutic_seasons?.length) return true;
  return Boolean(season) && exporter.frutic_seasons.includes(season as string);
};

type FruticShipmentParty = {
  shipment_entity?: {
    type?: string | null;
    name?: string | null;
  } | null;
};

type FruticShipment = {
  exporterRef?: string | null;
  exporterId?: number | null;
  client?: string | null;
  containerNumber?: string | null;
  date?: string | null;
  parties?: FruticShipmentParty[] | null;
  packing?: {
    name?: string | null;
  } | null;
  ship?: {
    name?: string | null;
    type?: string | null;
    etd?: string | null;
    eta?: string | null;
    originPort?: string | null;
    destinyPort?: string | null;
    originCountry?: string | null;
    destinyCountry?: string | null;
  } | null;
};

type FruticShipmentBoxing = {
  exporterId?: number | null;
  shipment?: FruticShipment | null;
  quantity?: number | null;
  boxWeight?: number | null;
  variety?: string | null;
  color?: string | null;
  boxType?: string | null;
  boxLabel?: string | null;
  boxDescription?: string | null;
  producer_sag_code?: {
    producer?: {
      name?: string | null;
      nationalId?: string | null;
      group?: {
        name?: string | null;
      } | null;
    } | null;
    code?: string | null;
  } | null;
  receptionDate?: string | null;
  processDate?: string | null;
  processInternalId?: string | null;
  labeledVariety?: string | null;
};

const normalizeBoxValue = (value: string | null | undefined) =>
  (value || '').trim().toUpperCase();

const getFruticShipmentClient = (shipment?: FruticShipment | null) =>
  shipment?.parties?.find(
    (party) => party.shipment_entity?.type === 'Cliente'
  )?.shipment_entity?.name ||
  shipment?.client ||
  '';

export const fruticShipmentBoxingsToShipmentBoxWithMappings = (
  boxes: FruticShipmentBoxing[],
  mappings: BoxFormatMapping[]
) => {
  const mappingsByCode = new Map<string, BoxFormatMapping>();
  for (const mapping of mappings) {
    const code = normalizeBoxValue(mapping.frutic_code);
    if (!mappingsByCode.has(code)) mappingsByCode.set(code, mapping);
  }
  return boxes.map((box) => {
    const shipment = box.shipment;
    const mapping = mappingsByCode.get(normalizeBoxValue(box.boxType));
    const box_format = mapping?.cherry_box_format || 'NO ENCONTRADO';
    const box_label = box.boxLabel;
    const unit_weight = box.boxWeight;
    const receiver = getFruticShipmentClient(shipment) || 'NO DEFINIDO';

    return {
      ...box,
      reference_number: shipment?.exporterRef || '',
      receiver,
      container_number: shipment?.containerNumber || '',
      exporter_id: box.exporterId,
      color: getColorInCherryFormat(box.color),
      box_label,
      box_format,
      box_weight: unit_weight,
      unit_weight,
      box_type: box.boxType,
      box_description: box.boxDescription,
      producer_name: box.producer_sag_code?.producer?.name || 'NO DEFINIDO',
      producer_sag_code: box.producer_sag_code?.code || '',
      ship_name: shipment?.ship?.name || 'NO DEFINIDO',
      ship_type:
        shipment?.ship?.type == 'AEREO'
          ? 'A'
          : shipment?.ship?.type == 'TERRESTRE'
            ? 'T'
            : 'M',
      ship_etd: shipment?.ship?.etd || '',
      ship_eta: shipment?.ship?.eta || '',
      ship_origin_port: shipment?.ship?.originPort || 'NO DEFINIDO',
      ship_destination_port: shipment?.ship?.destinyPort || 'NO DEFINIDO',
      ship_origin_country: shipment?.ship?.originCountry || 'NO DEFINIDO',
      ship_destination_country: shipment?.ship?.destinyCountry || 'NO DEFINIDO',
      estimated_settlement_date: shipment?.ship?.eta || '',
      week_etd: `${getWeekKey(shipment?.ship?.etd || '')}`,
      week_eta: `${getWeekKey(shipment?.ship?.eta || '')}`,
      ship_trip_id: `${shipment?.ship?.name || 'NA'}-${shipment?.ship?.etd || ''}-${shipment?.ship?.eta || ''}`,
      producer_national_id: box.producer_sag_code?.producer?.nationalId || '',
      producer_group:
        box.producer_sag_code?.producer?.group?.name ||
        box.producer_sag_code?.producer?.name ||
        'NO DEFINIDO',
      reception_date: box.receptionDate,
      process_date: box.processDate,
      process_internal_id: box.processInternalId,
      labeled_variety: box.labeledVariety,
      packing_name: shipment?.packing?.name || 'NA',
      packing_etd: shipment?.date || null,
      complete_format: `${box_format}-${unit_weight}KG`,
    };
  });
};

export const fruticShipmentBoxingsToShipmentBoxes = (
  boxes: FruticShipmentBoxing[],
  speciesId: number
) => {
  return boxes.map((box) => {
    const shipment = box.shipment;
    const unit_weight = box.boxWeight;
    const receiver = getFruticShipmentClient(shipment) || 'NO DEFINIDO';

    return {
      ...box,
      reference_number: shipment?.exporterRef || '',
      receiver,
      container_number: shipment?.containerNumber || '',
      exporter_id: box.exporterId,
      color: speciesId == 2 ? getColorInCherryFormat(box.color) : box.color,
      box_label: box.boxLabel,
      box_weight: unit_weight,
      unit_weight,
      box_type: box.boxType,
      box_description: box.boxDescription,
      producer_name: box.producer_sag_code?.producer?.name || 'NO DEFINIDO',
      producer_sag_code: box.producer_sag_code?.code || '',
      ship_name: shipment?.ship?.name || 'NO DEFINIDO',
      ship_type:
        shipment?.ship?.type == 'AEREO'
          ? 'A'
          : shipment?.ship?.type == 'TERRESTRE'
            ? 'T'
            : 'M',
      ship_etd: shipment?.ship?.etd || '',
      ship_eta: shipment?.ship?.eta || '',
      ship_origin_port: shipment?.ship?.originPort || 'NO DEFINIDO',
      ship_destination_port: shipment?.ship?.destinyPort || 'NO DEFINIDO',
      ship_origin_country: shipment?.ship?.originCountry || 'NO DEFINIDO',
      ship_destination_country:
        shipment?.ship?.destinyCountry || 'NO DEFINIDO',
      estimated_settlement_date: shipment?.ship?.eta || '',
      week_etd: `${getWeekKey(shipment?.ship?.etd || '')}`,
      week_eta: `${getWeekKey(shipment?.ship?.eta || '')}`,
      ship_trip_id: `${shipment?.ship?.name}-${shipment?.ship?.etd}-${shipment?.ship?.eta}`,
      producer_national_id: box.producer_sag_code?.producer?.nationalId || '',
      producer_group:
        box.producer_sag_code?.producer?.group?.name ||
        box.producer_sag_code?.producer?.name ||
        'NO DEFINIDO',
      reception_date: box.receptionDate,
      process_date: box.processDate,
      labeled_variety: box.labeledVariety,
      process_internal_id: box.processInternalId,
      packing_name: shipment?.packing?.name || 'NA',
    };
  });
};

export const fruticShipmentsToCherryFormat = (shipments: FruticShipment[]) => {
  return shipments.map((shipment) => {
    const client = getFruticShipmentClient(shipment) || 'NO DEFINIDO';
    return {
      ...shipment,
      reference_number: shipment.exporterRef,
      exporter_id: shipment.exporterId,
      receiver: client,
      ship_name: shipment.ship?.name || 'NO DEFINIDO',
      ship_type:
        shipment.ship?.type == 'AEREO'
          ? 'A'
          : shipment.ship?.type == 'TERRESTRE'
            ? 'T'
            : 'M',
      ship_etd: shipment.ship?.etd || '',
      ship_eta: shipment.ship?.eta || '',
      estimated_settlement_date: shipment.ship?.eta || '',
      week_etd: `${getWeekKey(shipment.ship?.etd || '')}`,
      week_eta: `${getWeekKey(shipment.ship?.eta || '')}`,
      ship_trip_id: `${shipment.ship?.name}-${shipment.ship?.etd}-${shipment.ship?.eta}`,
    };
  });
};
